import { t } from "@lingui/macro";
import { atom } from "jotai";

import { reorderPrivateRounds } from "@/features/BookingRounds/utils/reorderPrivateRounds";

import {
  CreateSingleSlotBookingsInput,
  CreateMemberRoundInput,
  CreateGuestRoundInput,
  NewGuestRoundInput,
  CreateCustomerRoundInput,
} from "../../../hooks/useCreateNewGolfBooking/__generated__/useCreateNewGolfBookingMutation.graphql";
import { BookingWizard } from "../../../types";
import { BookingWizardDataAtom } from "../../stores";
import { processPrivateBookingRounds } from "../../utils/processPrivateBookingRounds";

type CustomerRoundInput = CreateCustomerRoundInput;

type PrivateRounds = {
  memberRounds: CreateMemberRoundInput[];
  guestRounds: CreateGuestRoundInput[];
  newGuestRounds: NewGuestRoundInput[];
};

const buildCustomerRounds = (
  rounds: BookingWizard.GolfRound[] | undefined,
  props: {
    courseLayoutId?: string;
    rateId: string | null;
    transportationTypeId: string | null;
    defaultTransportationTypeId?: string | null;
  },
  groupSize: number | undefined
): CustomerRoundInput[] => {
  const base: CustomerRoundInput[] = (rounds ?? []).map((round, index) => ({
    index,
    playerId: round.player.id,
    courseLayoutId: props.courseLayoutId,
    rateId: index === 0 ? props.rateId : null,
    transportationTypeId:
      round.transportationType?.id ?? props.transportationTypeId,
  }));

  if (groupSize && base.length < groupSize) {
    const remainingRounds = groupSize - base.length;
    const newRounds: CustomerRoundInput[] = Array.from(
      { length: remainingRounds },
      (_, i) => ({
        index: base.length + i,
        playerId: null,
        courseLayoutId: props.courseLayoutId,
        rateId: null,
        transportationTypeId:
          props.transportationTypeId ??
          props.defaultTransportationTypeId ??
          null,
      })
    );
    base.push(...newRounds);
  }

  return base;
};

const buildPrivateRounds = (
  rounds: BookingWizard.GolfRound[] | undefined,
  ownerClientId: string,
  golfProps: {
    courseLayout?: BookingWizard.CourseLayout;
    transportationTypeId: string | null;
  }
): PrivateRounds => {
  const allMemberRounds = processPrivateBookingRounds<CreateMemberRoundInput>(
    rounds,
    ["member", "memberHold"],
    golfProps
  );

  const guestRoundsRaw = processPrivateBookingRounds<CreateGuestRoundInput>(
    rounds,
    ["guest", "unknownGuest"],
    golfProps
  );

  const newGuestRoundsRaw = processPrivateBookingRounds<NewGuestRoundInput>(
    rounds,
    ["newGuest"],
    golfProps,
    (round) => {
      const safeFirst = round.player?.firstName?.trim() ?? "";
      const safeLast = round.player?.lastName?.trim() ?? "";
      const fullName = [safeFirst, safeLast].filter(Boolean).join(" ").trim();
      return {
        name: fullName.length > 0 ? fullName : t`New Guest`,
      };
    }
  );

  // Order: owner at index 0, then ALL guest rounds, then remaining member rounds.
  return reorderPrivateRounds({
    memberRounds: allMemberRounds,
    guestRounds: guestRoundsRaw,
    newGuestRounds: newGuestRoundsRaw,
    ownerClientId,
  });
};

export const createSingleSlotBookingsInputAtom =
  atom<CreateSingleSlotBookingsInput | null>((get) => {
    const bookingWizardData = get(BookingWizardDataAtom);
    if (!bookingWizardData || bookingWizardData.type !== "GolfBooking") {
      return null;
    }
    const { startSlotId, ownerClientId, rounds, groupSize } = bookingWizardData;

    const golfProps = {
      courseLayout: bookingWizardData.courseLayout,
      rateId: bookingWizardData.rateId ?? null,
      transportationTypeId: bookingWizardData.transportationTypeId,
      defaultTransportationTypeId:
        bookingWizardData.defaultTransportationTypeId,
      isPrivate: bookingWizardData.isPrivate,
    };

    const paymentCardOnFileId =
      bookingWizardData.paymentCardOnFileId ?? undefined;

    if (!startSlotId) {
      return null;
    }
    const bookingInput: CreateSingleSlotBookingsInput = {
      bookings: [
        {
          startSlotId,
          ...(golfProps.isPrivate
            ? buildPrivateRounds(rounds, ownerClientId, {
                courseLayout: golfProps.courseLayout,
                transportationTypeId: golfProps.transportationTypeId,
              })
            : {
                customerRounds: buildCustomerRounds(
                  rounds,
                  {
                    courseLayoutId: golfProps.courseLayout?.id,
                    rateId: golfProps.rateId,
                    transportationTypeId: golfProps.transportationTypeId,
                    defaultTransportationTypeId:
                      golfProps.defaultTransportationTypeId,
                  },
                  groupSize
                ),
              }),
          isDraft: true,
          ownerClientId,
          paymentCardOnFileId,
        },
      ],
      notifyMember: true,
    };

    return bookingInput;
  });
